import * as React from 'react';
import { GoogleBusinessReplyModal } from '.';
import {
	invalidateGetGoogleBusinessReviewsWithoutReplies,
	useGetGoogleBusinessReviewsWithoutReplies,
} from '../../../../queries';
import { THEME } from '../../../components-v2/Theme/theme';
import { ActiveGoogleBusinessReview } from '../../Happiness/DashboardGoogleBusinessProfileCard';

import { GoogleBusinessReplyMobileModal } from './mobileReplyModal';

interface IProps {
	isOpen: boolean;
	activeReview?: ActiveGoogleBusinessReview;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GoogleBusinessReplyModalControlWrapper = ({ isOpen, setIsOpen, activeReview }: IProps) => {
	const [reviews, setReviews] = React.useState<ActiveGoogleBusinessReview[]>([]);
	const [carouselIndex, setCarouselIndex] = React.useState(0);
	const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

	// Screen width detection
	React.useEffect(() => {
		if (typeof window === 'undefined') return;

		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const isMobile = windowWidth <= THEME.breakpoints.md;
	const {
		data: reviewData,
		status,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useGetGoogleBusinessReviewsWithoutReplies({
		enabled: isOpen,
		refetchOnWindowFocus: false,
	});
	const totalCount = reviewData?.pages?.[0]?.totalCount;
	const disableNext = !reviews?.length || carouselIndex + 1 === totalCount;
	const disablePrevious = !carouselIndex;
	const closeModal = () => {
		invalidateGetGoogleBusinessReviewsWithoutReplies();
		setIsOpen(false);
		setCarouselIndex(0);
	};

	const onSuccess = (reviewId: string) => {
		invalidateGetGoogleBusinessReviewsWithoutReplies();
		setReviews(reviews.filter(review => review.id !== reviewId));
		setCarouselIndex(0);
	};
	const carouselProgressText = `${carouselIndex + 1}/${totalCount}`;
	const goToNext = () => {
		if (disableNext) {
			return;
		}
		const shouldFetchNextPage = hasNextPage && !isFetchingNextPage && carouselIndex + 5 === reviews.length;
		if (shouldFetchNextPage) {
			fetchNextPage();
		}
		setCarouselIndex(carouselIndex + 1);
	};

	const goBack = () => {
		if (!disablePrevious) {
			setCarouselIndex(carouselIndex - 1);
		}
	};

	React.useEffect(() => {
		let recentReviews = reviewData?.pages
			? reviewData.pages
					.map(page => {
						return page.values;
					})
					.flat()
			: null;
		if (activeReview && !activeReview.reply) {
			recentReviews =
				recentReviews && recentReviews.length > 0
					? [activeReview, ...recentReviews.filter(review => review.id !== activeReview.id)]
					: [activeReview];
			setReviews(recentReviews);
		} else if (recentReviews) {
			const processedReviews = recentReviews.map(review => ({
				...review,
				includeGeneratedReply: activeReview?.id === review.id ? (activeReview.includeGeneratedReply ?? false) : false,
			}));
			setReviews(processedReviews);
		}
	}, [reviewData, activeReview]);

	// Effect to handle when modal opens with a specific review
	React.useEffect(() => {
		if (isOpen && activeReview && !activeReview.reply) {
			setCarouselIndex(0); // The activeReview will be at index 0 due to the effect above
		}
	}, [isOpen, activeReview]);
	const ModalComponent = isMobile ? GoogleBusinessReplyMobileModal : GoogleBusinessReplyModal;
	const currentReview =
		activeReview?.includeGeneratedReply && carouselIndex === 0
			? activeReview
			: reviews.length > 0
				? reviews[carouselIndex]
				: activeReview;
	return (
		<ModalComponent
			activeReview={currentReview}
			isLoadingReviews={status === 'pending'}
			isOpen={isOpen}
			goToNext={goToNext}
			goBack={goBack}
			disableNext={disableNext}
			disablePrevious={disablePrevious}
			onCloseModal={closeModal}
			onSuccess={onSuccess}
			remainingReviewsCount={totalCount}
			carouselProgressText={carouselProgressText}
		/>
	);
};
